import { useState } from "react";
import { Button, Card, Form, Modal } from "react-bootstrap";
import { useSnapshot } from "valtio";
import { useNavigate } from "react-router-dom";
import { engagementStore } from "../../../engagementStore";
import { EngagementService } from "@services/engagements/engagementService";
import { Paths } from "../../../../../paths";

const CONFIRMATION_PHRASE = "close engagement";

export const Close = () => {
  const { selectedEngagement } = useSnapshot(engagementStore);
  const navigate = useNavigate();

  /*
      A CREATE engagement never reaches an ad network, so closing it only marks
      it closed. Promising to archive campaigns and ad sets there would describe
      work that does not happen.
  */
  const isCreate = selectedEngagement?.type === "CREATE";

  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  const [closeSubmitted, setCloseSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isClosed = selectedEngagement?.status === "CLOSED";
  const isConfirmed = confirmText.toLowerCase().trim() === CONFIRMATION_PHRASE;

  const handleClose = async () => {
    if (!isConfirmed || !selectedEngagement?.id) return;
    setIsClosing(true);
    setError(null);
    try {
      const service = new EngagementService();
      await service.closeEngagement(selectedEngagement.id);
      setShowModal(false);
      setConfirmText("");
      setCloseSubmitted(true);
    } catch (err: any) {
      setError(err?.message || "Failed to close engagement");
    } finally {
      setIsClosing(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setConfirmText("");
    setError(null);
  };

  return (
    <>
      <Card className="custom-card overflow-hidden position-relative h-100">
        <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center p-5">
          {isClosed ? (
            <>
              <div className="mb-3">
                <i className="ri-checkbox-circle-line" style={{ fontSize: "3rem", color: "#6c757d" }} />
              </div>
              <h4 className="mb-2">Engagement Closed</h4>
              <p className="text-muted mb-4">
                {isCreate
                  ? "This engagement has been closed. Its content stays available to read."
                  : "This engagement has been closed. All network campaigns have been archived."}
              </p>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(`${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}`)}
              >
                Back to Engagements
              </Button>
            </>
          ) : closeSubmitted ? (
            <>
              <div className="mb-3">
                <i className="ri-loader-4-line" style={{ fontSize: "3rem", color: "#0d6efd" }} />
              </div>
              <h4 className="mb-2">Closing in Progress</h4>
              <p className="text-muted mb-4" style={{ maxWidth: 480 }}>
                {isCreate
                  ? "A background job is marking this engagement closed. It will show as closed here shortly."
                  : "A background job is archiving all network campaigns. The engagement will be marked as closed once all networks have been updated successfully."}
              </p>
            </>
          ) : (
            <>
              <div className="mb-3">
                <i className="ri-error-warning-line" style={{ fontSize: "3rem", color: "#dc3545" }} />
              </div>
              <h4 className="mb-2">Close Engagement</h4>
              <p className="text-muted mb-4" style={{ maxWidth: 480 }}>
                {isCreate
                  ? "Closing this engagement marks it closed and takes it out of the active list. Nothing is published anywhere, so there is nothing to tear down. This action cannot be undone."
                  : "Closing this engagement will archive all network presets across every launch. Campaigns, ad sets, and ads will be paused or archived on their respective networks. This action cannot be undone."}
              </p>
              <Button variant="danger" onClick={() => setShowModal(true)}>
                Close Engagement
              </Button>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={handleCancel}
        centered
        backdrop="static"
        style={{ background: "rgba(0,0,0,.5)" }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Close Engagement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This will permanently close <strong>{selectedEngagement?.name}</strong>
            {isCreate ? "." : " and archive all network campaigns."} This cannot be undone.
          </p>
          <Form.Group>
            <Form.Label>
              Type <strong>{CONFIRMATION_PHRASE}</strong> to confirm:
            </Form.Label>
            <Form.Control
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRMATION_PHRASE}
              autoFocus
              disabled={isClosing}
            />
          </Form.Group>
          {error && <p className="text-danger mt-2 mb-0">{error}</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel} disabled={isClosing}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleClose} disabled={!isConfirmed || isClosing}>
            {isClosing ? "Closing..." : "Close Engagement"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
